import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import Page from '../../common/Page'
import Card from '../../common/Card'
import $t from '../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Radio, Tooltip } from 'antd'
import Actions from '../../../actions'
import DatePicker from '../../common/DatePicker/DatePicker'
import isBetween from 'dayjs/plugin/isBetween'
import { STEPS } from 'consts/flowConsts'
import {
  getMemberEffectiveDate,
  getMemberQualificationReasonCode
} from 'utils/memberUtils'
import { QUALIFICATION_LINK, dateFormat } from 'consts'
import routes from 'consts/routes'
import {
  HAD_BABY,
  ADOPTED_OR_BEGAN_FOSTERING_CHILD,
  NONE_OF_THE_ABOVE,
  OTHER_QUALIFYING_LIFE_EVENT
} from 'consts/reasonsConsts'

import './Qualification.css'

dayjs.extend(isBetween)

const getDateValidationError = ({ selectedDate, members }) => {
  const startDate = dayjs()?.subtract(60, 'days')
  const endDate = dayjs()?.add(60, 'days')
  const isBetween = dayjs(selectedDate).isBetween(
    startDate?.subtract(1, 'days'),
    endDate
  )

  const isNotBornMemberAtDate = members?.some(member => {
    const memberDate = new Date(member.dob)
    const selected = new Date(selectedDate)
    return memberDate > selected
  })

  if (!isBetween) {
    return $t('errors.validation.invalidQualifyingDate', {
      startDate: startDate?.format('MM/DD/YYYY'),
      endDate: endDate?.format('MM/DD/YYYY')
    })
  }
  if (isNotBornMemberAtDate) {
    return $t('errors.validation.invalidDob')
  }
  return null
}

const Qualification = () => {
  const dispatch = useDispatch()

  const memberQualifyLifeEvent = useSelector(
    state => state.member?.memberQualifyLifeEvent
  )
  const qualificationReasons = useSelector(
    state => state.application?.qualificationReasons
  )
  const members = useSelector(state => state.member?.members)
  const locale = useSelector(state => state.locale?.locale)

  const [shouldShowAllReasons, setShouldShowAllReasons] = useState(false)
  const [selectedReason, setSelectedReason] = useState('')
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedCoverageDate, setSelectedCoverageDate] = useState(null)
  const [dateError, setDateError] = useState(null)
  const [reasons, setReasons] = useState([])

  const isHadABabyReasonSelected =
    selectedReason === HAD_BABY ||
    selectedReason === ADOPTED_OR_BEGAN_FOSTERING_CHILD
  const isNoneOfTheAboveReasonSelected = selectedReason === NONE_OF_THE_ABOVE
  const firstDateOfNextMonth = getMemberEffectiveDate(selectedDate)

  const isContinueButtonDisabled = isNoneOfTheAboveReasonSelected
    ? false
    : isHadABabyReasonSelected
    ? !selectedDate || !selectedCoverageDate || !!dateError
    : !selectedDate || !selectedReason || !!dateError

  useEffect(() => {
    if (!qualificationReasons) {
      dispatch(
        Actions.createAction(Actions.QUALIFICATION_REASONS_REQUESTED, {
          locale
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(
      Actions.createAction(Actions.QUALIFICATION_REASONS_REQUESTED, { locale })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  useEffect(() => {
    // update from state
    setSelectedReason(
      getMemberQualificationReasonCode(
        memberQualifyLifeEvent?.reason,
        qualificationReasons
      )
    )
    setSelectedDate(memberQualifyLifeEvent?.date)
    setSelectedCoverageDate(memberQualifyLifeEvent?.coverageStartDate)
    if (qualificationReasons) {
      setReasons(
        shouldShowAllReasons
          ? Object.values(qualificationReasons)
          : Object.values(qualificationReasons).slice(0, 5)
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualificationReasons])

  useEffect(() => {
    if (selectedReason && !isNoneOfTheAboveReasonSelected) {
      // scroll into view to the date after selecting a reason
      document
        ?.getElementById('qualifyDate')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedReason, isNoneOfTheAboveReasonSelected])

  const onShowAllReasons = () => {
    setShouldShowAllReasons(true)
    setReasons(qualificationReasons)
  }

  const onProceed = () => {
    dispatch(
      Actions.createAction(Actions.MEMBER_QUALIFY_LIFE_EVENT_CHANGED, {
        reason: selectedReason,
        date: selectedDate,
        coverageStartDate: selectedCoverageDate || firstDateOfNextMonth
      })
    )

    if (isNoneOfTheAboveReasonSelected) {
      // proceed to no-eligible qualification life event page
      dispatch(
        Actions.createAction(Actions.NAVIGATED_TO, routes.ENROLL_NO_ELIGIBLE)
      )
      return
    }

    // proceed to next step
    dispatch(
      Actions.createAction(Actions.STEP_COMPLETED, {
        step: STEPS.QUALIFICATION
      })
    )
  }

  const updateQualifyLifeEventDate = selectedDate => {
    if (!selectedDate) {
      setSelectedDate(selectedDate)
      return
    }

    const error = getDateValidationError({
      selectedDate,
      members
    })

    if (error) {
      setDateError(error)
      return
    }

    setDateError(null)
    setSelectedDate(selectedDate)
  }

  const getSubtitle = () => {
    return (
      <div className='sub column'>
        {$t('enroll.qualification.subtitle')}
        <div className='semi-bold'>
          {'\n'}
          {$t('enroll.qualification.subtitleBold', {
            startDate: dayjs().subtract(60, 'days').format(dateFormat),
            endDate: dayjs().add(60, 'days').format(dateFormat)
          })}
        </div>
      </div>
    )
  }

  return (
    <Page
      className='enroll-qualification'
      title={$t('enroll.qualification.title')}
      subtitle={getSubtitle()}
      canSkip={true}
    >
      <Card className='qualification-card'>
        <div className='qualification-header semi-bold'>
          {$t('enroll.qualification.headerReason')}
        </div>
        <Radio.Group
          onChange={e => setSelectedReason(e?.target?.value)}
          value={selectedReason}
        >
          {reasons?.map(reason => {
            return (
              <div className='row baseline' key={reason.code}>
                <Radio value={reason.code}>
                  {reason.reason_name_translated}
                </Radio>
                {reason.code === OTHER_QUALIFYING_LIFE_EVENT && (
                  <Tooltip
                    placement='top'
                    overlayClassName='noneOfTheAboveReasonTooltip'
                    title={
                      <div className='column center'>
                        {$t('enroll.qualification.noneOfTheAboveReasonTooltip')}
                        <Button
                          type='link'
                          onClick={() =>
                            window.open(QUALIFICATION_LINK, '_blank')
                          }
                        >
                          {$t(
                            'enroll.qualification.noneOfTheAboveReasonTooltipLink'
                          )}
                        </Button>
                      </div>
                    }
                  >
                    <i className='icon-info' />
                  </Tooltip>
                )}
              </div>
            )
          })}
        </Radio.Group>
        {!shouldShowAllReasons && (
          <Button
            type='link'
            className='show-all-reasons'
            onClick={onShowAllReasons}
          >
            {$t('enroll.qualification.showAllReasons')}
          </Button>
        )}
        {selectedReason && !isNoneOfTheAboveReasonSelected && (
          <>
            <div
              className='qualification-header date semi-bold'
              id='qualifyDate'
            >
              {$t('enroll.qualification.headerDate')}
            </div>
            <DatePicker
              key={'date-picker'}
              errorText={dateError}
              defaultDob={selectedDate || ''}
              setSelectedDate={updateQualifyLifeEventDate}
            />
          </>
        )}
        {selectedDate && isHadABabyReasonSelected && (
          <>
            <div className='qualification-header date semi-bold'>
              {$t('enroll.qualification.headerStartDate')}
            </div>
            <Radio.Group
              className='row'
              onChange={e => setSelectedCoverageDate(e?.target?.value)}
              value={selectedCoverageDate}
            >
              <Radio key={selectedDate} value={selectedDate}>
                {selectedDate}
              </Radio>
              <Radio key={firstDateOfNextMonth} value={firstDateOfNextMonth}>
                {firstDateOfNextMonth}
              </Radio>
            </Radio.Group>
          </>
        )}
      </Card>
      <div className='enroll-qualification-footer'>
        <div className='enroll-qualification-note'>
          {$t('enroll.qualification.note')}
        </div>
        <Button
          className='continue-btn'
          type='primary'
          onClick={onProceed}
          disabled={!!isContinueButtonDisabled}
        >
          {$t('common.continue')}
        </Button>
      </div>
    </Page>
  )
}

export default Qualification
